import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Plus, Edit3, Send, Trash2, BarChart2, Copy, Settings, Folder, X, FolderOpen } from "lucide-react";
import { useApp } from "../../context/formflow/AppContext";
import { ACCENT } from "../../constants/formflow/fieldTypes";
import axiosInstance from "../../api/axiosInstance";

function getSubmissionStatus(submission) {
  const signers = submission?.signers || [];
  const total = signers.length;
  const completed = signers.filter((signer) => signer.status === "completed" || signer.status === "signed").length;
  const viewed = signers.some((signer) => signer.status === "viewed" || signer.viewed_at);

  if (total > 0 && completed === total) return "completed";
  if (completed > 0) return "partially_filled";
  if (viewed) return "viewed";
  return "sent";
}

// Skeleton components
const SkeletonFolderCard = () => (
  <div className="bg-white rounded-xl p-4 border-2 border-gray-200 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="w-7 h-7 bg-gray-200 rounded"></div>
      <div className="w-8 h-5 bg-gray-200 rounded-full"></div>
    </div>
    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
  </div>
);

const SkeletonFormCard = () => (
  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-6 relative overflow-hidden">
      <div className="h-4 bg-slate-600 rounded w-32 mb-2"></div>
      <div className="h-6 bg-slate-600 rounded w-48 mb-4"></div>
      <div className="flex gap-2">
        <div className="h-6 bg-slate-600 rounded w-16"></div>
        <div className="h-6 bg-slate-600 rounded w-16"></div>
      </div>
    </div>
    <div className="p-4">
      <div className="flex justify-between mb-3">
        <div className="h-3 bg-gray-200 rounded w-40"></div>
        <div className="h-3 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="flex gap-2 mb-3">
        <div className="h-8 bg-gray-200 rounded flex-1"></div>
        <div className="h-8 bg-gray-200 rounded flex-1"></div>
        <div className="h-8 bg-gray-200 rounded flex-1"></div>
        <div className="h-8 bg-gray-200 rounded w-10"></div>
        <div className="h-8 bg-gray-200 rounded w-10"></div>
      </div>
    </div>
  </div>
);

export function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useApp();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [updatingFolderFormId, setUpdatingFolderFormId] = useState(null);

  // Compute stats
  const submissions = forms.flatMap((form) => form.submissions || []);
  const totalSubmissions = submissions.length;
  const viewedSubmissions = submissions.filter((sub) => getSubmissionStatus(sub) === "viewed").length;
  const partiallyFilledSubmissions = submissions.filter((sub) => getSubmissionStatus(sub) === "partially_filled").length;
  const completedSubmissions = submissions.filter((sub) => getSubmissionStatus(sub) === "completed").length;
  const downloadedSubmissions = submissions.filter((sub) => sub.downloaded_at).length;

  const stats = [
    { label: "Sent", value: totalSubmissions, color: "#10b981" },
    { label: "Viewed", value: viewedSubmissions, color: "#3b82f6" },
    { label: "Filled Partially", value: partiallyFilledSubmissions, color: "#f59e0b" },
    { label: "Completed", value: completedSubmissions, color: "#22c55e" },
    { label: "Downloaded", value: downloadedSubmissions, color: "#8b5cf6" },
  ];

  // Helper: get folder ID for a given form
  const getFolderIdForForm = (formId) => {
    const folder = folders.find((f) => f.form_ids?.includes(formId));
    return folder ? folder.id : null;
  };

  // Helper: count forms per folder
  const getFormCountForFolder = (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    return folder?.form_count || 0;
  };

  // Fetch data
  const fetchForms = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get("/api/formflow/forms?limit=1000");
      if (!data.success) {
        showToast("Failed to load forms", "error");
        return;
      }
      setForms(data.forms || []);
    } catch (err) {
      console.error(err);
      showToast("Error loading forms", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const { data } = await axiosInstance.get("/api/formflow/folders");
      if (data.success) {
        setFolders(data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Read folder from URL
  useEffect(() => {
    if (folders?.length) {
      const folderParam = searchParams.get("folder");
      if (folderParam) {
        const folderId = parseInt(folderParam, 10);
        if (folders.some((f) => f.id === folderId)) {
          setActiveFolder(folderId);
        } else {
          setActiveFolder(null);
          setSearchParams({});
        }
      } else {
        setActiveFolder(null);
      }
    }
  }, [searchParams, folders]);

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    fetchForms();
  }, []);

  // Handlers (unchanged)
  const handleDuplicate = async (formId) => {
    try {
      setDuplicating(formId);
      const { data } = await axiosInstance.post(`/api/formflow/forms/${formId}/duplicate`);
      if (!data.success) {
        showToast("Failed to duplicate form", "error");
        return;
      }
      if (data.form) {
        setForms((prev) => [data.form, ...prev]);
      } else {
        fetchForms();
      }
      showToast("Form duplicated", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to duplicate form", "error");
    } finally {
      setDuplicating(null);
    }
  };

  const handleDeleteForm = async () => {
    try {
      await axiosInstance.delete(`/api/formflow/forms/${confirmDel}`);
      setForms((prev) => prev.filter((f) => f.id !== confirmDel));
      await fetchFolders();
      setConfirmDel(null);
      showToast("Form deleted", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to delete form", "error");
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showToast("Folder name is required", "error");
      return;
    }
    try {
      setCreatingFolder(true);
      const { data } = await axiosInstance.post("/api/formflow/folders", { name: newFolderName.trim() });
      if (data.success) {
        await fetchFolders();
        showToast("Folder created", "success");
        setShowCreateFolderModal(false);
        setNewFolderName("");
      } else {
        showToast("Failed to create folder", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error creating folder", "error");
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await axiosInstance.delete(`/api/formflow/folders?folderId=${folderToDelete}`);
      await fetchFolders();
      if (activeFolder === folderToDelete) {
        setSearchParams({});
        setActiveFolder(null);
      }
      showToast("Folder deleted", "success");
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || "Failed to delete folder";
      showToast(message, "error");
    } finally {
      setFolderToDelete(null);
    }
  };

  const handleUpdateFormFolder = async (formId, targetFolderId) => {
    const originalForms = [...forms];
    setUpdatingFolderFormId(formId);
    try {
      await axiosInstance.put(`/api/formflow/assign-folder?formId=${formId}&folderId=${targetFolderId || ""}`);
      await fetchFolders();
      showToast("Form moved", "success");
    } catch (err) {
      console.error(err);
      setForms(originalForms);
      showToast("Failed to update folder", "error");
    } finally {
      setUpdatingFolderFormId(null);
    }
  };

  const handleFolderClick = (folderId) => {
    if (folderId === null) {
      setSearchParams({});
    } else {
      setSearchParams({ folder: folderId.toString() });
    }
    setActiveFolder(folderId);
  };

  const isFormInActiveFolder = (form) => {
    if (!activeFolder) return true;
    const folder = folders.find((f) => f.id === activeFolder);
    return folder?.form_ids?.includes(form.id) ?? false;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="w-full bg-white border-b border-gray-200">
        <div className="px-10 h-16 flex items-center justify-between shadow-sm max-w-7xl mx-auto">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: ACCENT }}>
              <FileText size={18} color="white" />
            </div>
            <div>
              <div className="text-base font-bold text-gray-900 leading-none">FormFlow</div>
              <div className="text-[11px] text-gray-400">Document Form Builder</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/formflow/settings")} className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 px-4 py-2.5 rounded-lg cursor-pointer text-sm font-semibold hover:bg-gray-50 transition-colors">
              <Settings size={16} /> Settings
            </button>
            <button onClick={() => navigate("/formflow/builder")} className="flex items-center gap-2 bg-[#dc2626] text-white border-none px-5 py-2.5 rounded-lg cursor-pointer text-sm font-semibold shadow-md hover:opacity-90 transition-opacity">
              <Plus size={16} /> New Form
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="py-8 px-10 max-w-7xl mx-auto">
        {/* Stats cards */}
        <div className="grid grid-cols-5 gap-5 mb-9">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-5.5 shadow-sm border border-gray-100 flex items-center gap-4.5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl font-bold" style={{ background: s.color + "18", color: s.color }}>
                {s.value}
              </div>
              <div className="text-sm text-gray-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Folders Grid with Skeleton */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Folders</h2>
            <button onClick={() => setShowCreateFolderModal(true)} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium">
              <Plus size={16} /> New Folder
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {foldersLoading ? (
              // Show 4 skeleton folder cards while loading
              Array(4)
                .fill()
                .map((_, i) => <SkeletonFolderCard key={i} />)
            ) : (
              <>
                {/* All Forms card */}
                <div onClick={() => handleFolderClick(null)} className={`cursor-pointer bg-white rounded-xl p-4 border-2 transition-all hover:shadow-md ${activeFolder === null ? "border-red-500 bg-red-50" : "border-gray-200"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <FolderOpen size={28} className={activeFolder === null ? "text-red-500" : "text-gray-500"} />
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{forms.length}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800">All Forms</h3>
                  <p className="text-xs text-gray-500 mt-1">All your documents</p>
                </div>

                {/* Folder cards */}
                {folders.map((folder) => (
                  <div key={folder.id} onClick={() => handleFolderClick(folder.id)} className={`cursor-pointer bg-white rounded-xl p-4 border-2 transition-all hover:shadow-md ${activeFolder === folder.id ? "border-red-500 bg-red-50" : "border-gray-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Folder size={28} className={activeFolder === folder.id ? "text-red-500" : "text-gray-500"} />
                      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{folder.form_count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 truncate">{folder.name}</h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToDelete(folder.id);
                        }}
                        className="text-gray-400 hover:text-red-500 transition"
                        title="Delete folder"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Section header */}
        <div className="flex justify-between items-center mb-5">
          <h2 className="m-0 text-xl font-bold text-gray-900">Your Forms</h2>
          <button onClick={() => navigate(`/formflow/submissions`)} className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg bg-white cursor-pointer text-xs text-gray-500 font-medium hover:bg-gray-50">
            <BarChart2 size={14} /> Submissions Sent
          </button>
        </div>

        {/* Forms list with skeleton */}
        {loading ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-5">
            {Array(6)
              .fill()
              .map((_, i) => (
                <SkeletonFormCard key={i} />
              ))}
          </div>
        ) : forms.filter(isFormInActiveFolder).length === 0 ? (
          <div className="bg-white rounded-2xl !py-18 px-8 text-center border-2 border-dashed border-gray-200">
            <div className="text-6xl mb-4 mt-8">📋</div>
            <h3 className="m-0 mb-2 text-xl text-gray-700">No forms found</h3>
            <p className="m-0 mb-6 text-gray-400 text-[15px]">{activeFolder ? "This folder is empty." : "Create your first form template to start collecting signatures"}</p>
            {!activeFolder && (
              <button onClick={() => navigate("/formflow/builder")} className="inline-flex items-center gap-2 bg-[#dc2626] text-white border-none py-3 px-7 rounded-lg cursor-pointer text-sm font-semibold">
                <Plus size={16} /> Create Form
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-5">
            {forms.filter(isFormInActiveFolder).map((form) => {
              const subs = form.submissions || [];
              const sentCount = subs.length;
              const completedCount = subs.filter((s) => getSubmissionStatus(s) === "completed").length;
              const downloadedCount = subs.filter((s) => s.downloaded_at).length;
              const currentFolderId = getFolderIdForForm(form.id);
              return (
                <div key={form.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-6 relative overflow-hidden">
                    <div className="absolute -top-5 -right-5 opacity-5">
                      <FileText size={110} color="white" />
                    </div>
                    <div className="text-[11px] text-slate-300 font-semibold uppercase tracking-wide mb-1.5">
                      {form.pdfName || "Document Form"} | ID: {form.id}
                    </div>
                    <h3 className="m-0 mb-3.5 text-white text-lg font-semibold">{form.name}</h3>
                    <div className="flex gap-1.5 flex-wrap">
                      {form?.roles?.map((role) => (
                        <span key={role.id} className="bg-white/15 text-white/85 py-0.5 px-2.5 rounded-full text-xs font-medium">
                          {role.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between mb-3.5 text-xs text-gray-500">
                      <span>
                        <span>
                          {" "}
                          · <b className="text-gray-700">{form.fields?.length}</b> fields
                        </span>
                        <span>
                          {" "}
                          · <b className="text-gray-700">{sentCount}</b> sent
                        </span>
                        <span>
                          {" "}
                          · <b className="text-gray-700">{completedCount}</b> completed
                        </span>
                        <span>
                          {" "}
                          · <b className="text-gray-700">{downloadedCount}</b> downloaded
                        </span>
                      </span>
                      <span>{new Date(form.created_at).toLocaleString()}</span>
                    </div>

                    {/* Folder dropdown */}
                    <div className="mb-3 flex items-center gap-2">
                      <Folder size={14} className="text-gray-400" />
                      <select value={currentFolderId || ""} onChange={(e) => handleUpdateFormFolder(form.id, e.target.value)} disabled={updatingFolderFormId === form.id} className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-300">
                        <option value="">No folder</option>
                        {folders.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                      {updatingFolderFormId === form.id && <span className="text-xs text-gray-400 animate-pulse">Updating...</span>}
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/formflow/builder?id=${form.id}`, { state: { form } })} className="flex-1 py-2 rounded-lg border border-gray-200 bg-white cursor-pointer text-xs font-medium text-gray-700 flex items-center justify-center gap-1 hover:bg-gray-50">
                        <Edit3 size={13} /> Edit
                      </button>
                      <button onClick={() => handleDuplicate(form.id)} disabled={duplicating === form.id} className="flex-1 py-2 rounded-lg border border-gray-200 bg-white cursor-pointer text-xs font-medium text-gray-700 flex items-center justify-center gap-1 hover:bg-gray-50 disabled:opacity-60">
                        <Copy size={13} /> Duplicate
                      </button>
                      <button onClick={() => navigate(`/formflow/send/${form.id}`)} className="flex-1 py-2 rounded-lg border-none bg-[#dc2626] cursor-pointer text-xs font-semibold text-white flex items-center justify-center gap-1 hover:opacity-90">
                        <Send size={13} /> Send
                      </button>
                      <button onClick={() => navigate(`/formflow/submissions/${form.id}`)} className="py-2 px-3 rounded-lg border border-gray-200 bg-white cursor-pointer text-gray-500 flex items-center hover:bg-gray-50">
                        <BarChart2 size={14} />
                      </button>
                      <button onClick={() => setConfirmDel(form.id)} className="py-2 px-3 rounded-lg border border-red-100 bg-red-50 cursor-pointer text-red-500 flex items-center hover:bg-red-100">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals (unchanged) */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-10">
          <div className="bg-white rounded-2xl p-8 w-96 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <Folder size={20} className="text-gray-600" />
              <h3 className="m-0 text-lg text-gray-900">Create New Folder</h3>
            </div>
            <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" className="w-full border border-gray-200 rounded-lg px-4 py-2 mb-6 text-sm focus:outline-none focus:ring-2 focus:ring-red-300" autoFocus />
            <div className="flex gap-2.5 justify-end">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName("");
                }}
                className="px-4.5 py-2.5 rounded-lg border border-gray-200 bg-white cursor-pointer font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button onClick={handleCreateFolder} disabled={creatingFolder} className="px-4.5 py-2.5 rounded-lg border-none bg-red-500 text-white cursor-pointer font-semibold hover:bg-red-600 disabled:opacity-60">
                {creatingFolder ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {folderToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-10">
          <div className="bg-white rounded-2xl p-8 w-96 shadow-xl">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                <Trash2 size={20} />
              </div>
              <h3 className="m-0 text-lg text-gray-900">Delete Folder</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6">This folder will be deleted. Forms inside will not be deleted but will be moved to "No folder". This action cannot be undone.</p>
            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setFolderToDelete(null)} className="px-4.5 py-2.5 rounded-lg border border-gray-200 bg-white cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDeleteFolder} className="px-4.5 py-2.5 rounded-lg border-none bg-red-500 text-white cursor-pointer font-semibold hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-10">
          <div className="bg-white rounded-2xl p-8 w-95 shadow-xl">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                <Trash2 size={20} />
              </div>
              <h3 className="m-0 text-lg text-gray-900">Delete Form</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6">This will permanently delete the form and all its submissions. This cannot be undone.</p>
            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setConfirmDel(null)} className="px-4.5 py-2.5 rounded-lg border border-gray-200 bg-white cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDeleteForm} className="px-4.5 py-2.5 rounded-lg border-none bg-red-500 text-white cursor-pointer font-semibold hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
